/**
 * XML Document Parser
 * Parses XML document definitions (like demo_letter.xml) and extracts document structure
 */

import { decodeHtmlEntities } from './xmlBlockImporter';

/**
 * Parse XML document definition and extract structure
 * @param {string} xmlString - XML content as string
 * @returns {Object} Parsed document definition with metadata, names, and sections
 */
export const parseXMLDocumentDefinition = (xmlString) => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
    
    // Check for parsing errors
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      throw new Error('Invalid XML format: ' + parserError.textContent);
    }
    
    // Find document definition
    const docDef = xmlDoc.querySelector('sld\\:document-definition, document-definition');
    if (!docDef) {
      throw new Error('No document definition found in XML');
    }
    
    // Extract document-level attributes
    const author = docDef.getAttribute('author') || '';
    const template = docDef.getAttribute('template') || '';
    const version = docDef.getAttribute('version') || '';
    const modificationDate = docDef.getAttribute('modification-date') || '';
    
    // Extract names per language - only from direct children of document definition
    const names = {};
    // Find the direct sld:names or names child of document definition (not nested in sections)
    // Iterate through direct children to find names element, avoiding nested names in sections
    const directChildren = Array.from(docDef.childNodes || []);
    let docNamesEl = null;
    for (const child of directChildren) {
      if (child.nodeType === 1) { // Element node
        const tagName = child.tagName || child.localName;
        if (tagName === 'sld:names' || tagName === 'names' || tagName.endsWith(':names')) {
          docNamesEl = child;
          break;
        }
      }
    }
    
    if (docNamesEl) {
      const nameElements = docNamesEl.querySelectorAll('sld\\:name, name');
      nameElements.forEach((nameEl) => {
        // Try multiple ways to get the language attribute (same as content extraction)
        const lang = nameEl.getAttribute('xml:lang') || 
                     nameEl.getAttributeNS('http://www.w3.org/XML/1998/namespace', 'lang') ||
                     nameEl.getAttribute('xml\\:lang') || 
                     nameEl.getAttribute('lang') || 
                     'en'; // Default to 'en' for names if missing
        names[lang] = nameEl.textContent.trim();
      });
    }
    
    // Extract metadata
    const metadata = {};
    const metaFields = docDef.querySelectorAll('sld\\:meta-data > sld\\:field, meta-data > field');
    metaFields.forEach((field) => {
      const key = field.getAttribute('key');
      const valueEl = field.querySelector('sld\\:item > sld\\:value, item > value');
      if (key && valueEl) {
        metadata[key] = valueEl.textContent.trim();
      }
    });
    
    // Extract sections
    const sections = [];
    const sectionElements = docDef.querySelectorAll('sld\\:sections > sld\\:section, sections > section');
    
    sectionElements.forEach((sectionEl) => {
      const sectionId = sectionEl.getAttribute('id');
      if (!sectionId) return;
      
      // Extract section names per language
      const sectionNames = {};
      const sectionNameEls = sectionEl.querySelectorAll('sld\\:names > sld\\:name, names > name');
      sectionNameEls.forEach((nameEl) => {
        const lang = nameEl.getAttribute('xml\\:lang') || nameEl.getAttribute('lang') || 'en';
        sectionNames[lang] = nameEl.textContent.trim();
      });
      
      // Extract block entries from standard-blocks
      const blocks = [];
      const blockEntries = sectionEl.querySelectorAll('sld\\:standard-blocks > sld\\:block-entry, standard-blocks > block-entry');
      
      blockEntries.forEach((blockEntry) => {
        const blockEl = blockEntry.querySelector('sld\\:block, block');
        if (!blockEl) return;
        
        // Extract iytxtbs value
        // Use alternative approach: find all fields and check key attribute
        // (CSS selectors don't support attribute selectors with @ syntax)
        let iytxtbsField = null;
        const allFields = blockEl.querySelectorAll('sld\\:field, field');
        for (let i = 0; i < allFields.length; i++) {
          const field = allFields[i];
          const key = field.getAttribute('key');
          if (key === 'iytxtbs') {
            iytxtbsField = field;
            break;
          }
        }
        
        const iytxtbsValue = iytxtbsField 
          ? (iytxtbsField.querySelector('sld\\:item > sld\\:value, item > value')?.textContent || '').trim()
          : '';
        
        // Extract block path
        const blockPath = blockEl.getAttribute('path') || '';
        const condition = blockEl.getAttribute('condition') || '';
        
        // Extract content per language
        const contents = {};
        const contentEls = blockEl.querySelectorAll('sld\\:contents > sld\\:content, contents > content');
        contentEls.forEach((contentEl) => {
          // Try multiple ways to get the language attribute
          // xml:lang is a namespaced attribute, try both direct and namespaced access
          let lang = contentEl.getAttribute('xml:lang') || 
                     contentEl.getAttributeNS('http://www.w3.org/XML/1998/namespace', 'lang') ||
                     contentEl.getAttribute('xml\\:lang') || 
                     contentEl.getAttribute('lang') || 
                     null; // Don't default to 'en' - if no lang, skip this content
          
          // If no language attribute found, skip this content (don't default to 'en')
          if (!lang) {
            return; // Skip this content element
          }
          
          const dataEl = contentEl.querySelector('sld\\:data, data');
          if (dataEl) {
            const rawContent = dataEl.textContent || '';
            const decodedContent = decodeHtmlEntities(rawContent);
            if (decodedContent) {
              contents[lang] = decodedContent;
            }
          }
        });
        
        blocks.push({
          iytxtbs: iytxtbsValue,
          path: blockPath,
          condition,
          contents,
        });
      });
      
      sections.push({
        id: sectionId,
        names: sectionNames,
        blocks,
      });
    });
    
    return {
      author,
      template,
      version,
      modificationDate,
      names,
      metadata,
      sections,
    };
  } catch (error) {
    console.error('Error parsing XML document definition:', error);
    throw error;
  }
};

/**
 * Parse multiple document definitions from XML
 * @param {string} xmlString - XML content that may contain multiple document definitions
 * @returns {Array} Array of parsed document definitions
 */
export const parseXMLDocumentDefinitions = (xmlString) => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
    
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      throw new Error('Invalid XML format: ' + parserError.textContent);
    }
    
    // Find all document definitions
    const docDefs = xmlDoc.querySelectorAll('sld\\:document-definition, document-definition');
    const definitions = [];
    
    docDefs.forEach((docDef) => {
      // Create a temporary XML document with just this definition
      const tempXml = `<?xml version="1.0" encoding="utf-8"?><root>${docDef.outerHTML}</root>`;
      const parsed = parseXMLDocumentDefinition(tempXml);
      definitions.push(parsed);
    });
    
    return definitions;
  } catch (error) {
    console.error('Error parsing multiple XML document definitions:', error);
    throw error;
  }
};

/**
 * Get content for a specific section and language
 * @param {Object} documentDefinition - Parsed document definition
 * @param {string} sectionId - Section ID (pod, reference, object, body, signature)
 * @param {string} language - Language code (fr, nl, en, de)
 * @returns {Array} Array of block contents for the section
 */
export const getSectionContent = (documentDefinition, sectionId, language) => {
  const section = documentDefinition.sections.find(s => s.id === sectionId);
  if (!section) {
    return [];
  }
  
  const filtered = section.blocks
    .filter(block => block.contents[language] && block.contents[language].trim() !== '');
  
  return filtered.map(block => ({
    iytxtbs: block.iytxtbs,
    content: block.contents[language],
    path: block.path,
    condition: block.condition,
  }));
};

